import { useCallback, useEffect, useState } from "react";
import type { PlatformCloudAccess, RcaClient } from "@rca/sdk";

/**
 * 基盤の設定（クラウドに入るための、我々の側の資格）。
 *
 * ★ **「触れるか」はサーバに聞く。** 画面の側で「このテナントなら出す」と
 * 判断すると、判断が 2 か所になり片方だけ緩む。口が 403（触れない）や
 * 404（口が生えていない）を返したら、その人には**無いもの**として扱う。
 */
export interface PlatformCloudAccessState {
  /** この人に見せてよいか。★ 口が答えるまでは false。 */
  readonly available: boolean;
  readonly items: readonly PlatformCloudAccess[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly save: (cloud: string, setting: Record<string, string>) => Promise<void>;
  readonly remove: (cloud: string) => Promise<void>;
}

export function usePlatformCloudAccess(
  client: RcaClient,
  enabled: boolean,
): PlatformCloudAccessState {
  const [items, setItems] = useState<readonly PlatformCloudAccess[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setItems((await client.platformCloudAccess.list()).items);
    } catch {
      // ★ 触れない人には「無い」。理由を画面に出さない（403 と 404 を
      // 区別して見せる意味が無く、見せると口の有無が漏れる）。
      setItems(null);
    }
  }, [client]);

  useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, reload]);

  const run = useCallback(
    async (work: () => Promise<void>) => {
      setBusy(true);
      setError(null);
      try {
        await work();
        await reload();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const save = useCallback(
    async (cloud: string, setting: Record<string, string>) =>
      run(() => client.platformCloudAccess.put(cloud, setting)),
    [client, run],
  );

  const remove = useCallback(
    async (cloud: string) => run(() => client.platformCloudAccess.remove(cloud)),
    [client, run],
  );

  return { available: items !== null, items: items ?? [], busy, error, save, remove };
}
